import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Navbar from '../../components/layout/Navbar/Navbar';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import EditarContaHeader from '../../components/features/editarConta/EditarContaHeader';
import { getCargoIdSidebar } from '../../constants/sidebarConfig';
import apiService from '../../services/api';
import { usePopup } from '../../components/common/Popup';
import PasswordInput from '../../components/common/PasswordInput';
import InfoTooltip from '../../components/common/InfoTooltip';
import '../../components/features/users/user-table.css';

const SIDEBAR_WIDTH = 300;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f9f9f9;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  margin-top: 60px;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${SIDEBAR_WIDTH}px;
  overflow-y: auto;
  background: #ffffff;
`;

const ContentContainer = styled.div`
  padding: 0px 0px;
`;

const SectionContainer = styled.div`
  background: #ffffff;
  overflow: hidden;
  margin-bottom: 24px;
`;

const FormSection = styled.div`
  padding: 0px 40px 0px 40px;
  margin-top: 20px;
`;

const FormTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #6ab8a8;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const Label = styled.label`
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background-color: #ffffff;

  &:focus {
    outline: none;
    border-color: #6ab8a8;
    box-shadow: 0 0 0 2px rgba(106, 184, 168, 0.2);
  }
`;

const SenhaField = styled(PasswordInput)`
  width: 100%;
  max-width: 400px;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  max-width: 400px;
`;

const SaveButton = styled.button`
  background: linear-gradient(135deg, #6ab8a8 0%, #4a9d8f 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EditarConta = () => {
  const popup = usePopup();
  const [user, setUser] = useState(null);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [passwordForm, setPasswordForm] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: '',
  });
  const [emailForm, setEmailForm] = useState({
    emailAtual: '',
    novoEmail: '',
    confirmarEmail: '',
  });
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
        if (userData && userData.email) {
          setEmailForm(prev => ({
            ...prev,
            emailAtual: userData.email,
          }));
        }
        const filteredItems = getCargoIdSidebar(userData.cargoId);
        setSidebarItems(filteredItems);
      } catch (err) {
        console.error('Erro ao buscar usuário:', err);
      }
    };

    fetchUser();
  }, []);

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleEmailChange = (e) => {
    setEmailForm({
      ...emailForm,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.novaSenha !== passwordForm.confirmarSenha) {
      await popup.alert({
        icon: 'warning',
        message: 'As senhas não coincidem.',
      });
      return;
    }

    setIsSubmittingPassword(true);
    try {
      const response = await apiService.updatePassword({
        senhaAtual: passwordForm.senhaAtual,
        novaSenha: passwordForm.novaSenha,
        confirmarSenha: passwordForm.confirmarSenha,
      });
      await popup.alert({
        icon: 'success',
        message: response?.message || 'Senha atualizada com sucesso.',
      });
      setPasswordForm({
        senhaAtual: '',
        novaSenha: '',
        confirmarSenha: '',
      });
    } catch (error) {
      await popup.alert({
        icon: 'warning',
        title: 'Erro ao atualizar senha',
        message: error.message || 'Tente novamente.',
      });
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (emailForm.novoEmail !== emailForm.confirmarEmail) {
      await popup.alert({
        icon: 'warning',
        message: 'Os e-mails não coincidem.',
      });
      return;
    }

    setIsSubmittingEmail(true);
    try {
      const response = await apiService.updateEmail({
        novoEmail: emailForm.novoEmail,
        confirmarEmail: emailForm.confirmarEmail,
      });

      await popup.alert({
        icon: 'success',
        message: 'E-mail atualizado com sucesso.',
      });
      setEmailForm({
        emailAtual: response?.email || emailForm.novoEmail,
        novoEmail: '',
        confirmarEmail: '',
      });
    } catch (error) {
      await popup.alert({
        icon: 'warning',
        title: 'Erro ao atualizar e-mail',
        message: error.message || 'Tente novamente.',
      });
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  return (
    <PageWrapper>
      <Navbar 
        onLogout={apiService.logout}
        showHome={true}
        showNotifications={true}
        showProfile={true}
        showLogout={true}
        user={user}
      />
      <ContentWrapper>
        <Sidebar items={sidebarItems} />
        <MainContent>
          <EditarContaHeader />
          <ContentContainer>
            <SectionContainer>
              <div className="table-header">
                <h4 className="pending-title">Senha e segurança</h4>
              </div>
              
              <FormSection>
                <FormTitle>Alterar Senha</FormTitle>
                <form onSubmit={handlePasswordSubmit}>
                  <FormGroup>
                    <Label htmlFor="senhaAtual">Senha atual</Label>
                    <SenhaField
                      inputAs={Input}
                      id="senhaAtual"
                      name="senhaAtual"
                      value={passwordForm.senhaAtual}
                      onChange={handlePasswordChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <LabelRow>
                      <Label htmlFor="novaSenha" style={{ marginBottom: 0 }}>Nova senha</Label>
                      <InfoTooltip
                        label="Requisitos da senha"
                        ariaLabel="Requisitos da senha"
                      >
                        <br />
                        Mínimo de 8 caracteres, incluindo letra maiúscula, letra
                        minúscula, número e caractere especial (ex.: ! @ # $ %).
                      </InfoTooltip>
                    </LabelRow>
                    <SenhaField
                      inputAs={Input}
                      id="novaSenha"
                      name="novaSenha"
                      value={passwordForm.novaSenha}
                      onChange={handlePasswordChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="confirmarSenha">Redigite a nova senha</Label>
                    <SenhaField
                      inputAs={Input}
                      id="confirmarSenha"
                      name="confirmarSenha"
                      value={passwordForm.confirmarSenha}
                      onChange={handlePasswordChange}
                      required
                    />
                  </FormGroup>
                  <ButtonContainer>
                    <SaveButton type="submit" disabled={isSubmittingPassword}>
                      {isSubmittingPassword ? 'Salvando...' : 'Salvar'}
                    </SaveButton>
                  </ButtonContainer>
                </form>
              </FormSection>

              <FormSection>
                <FormTitle>Alterar E-mail</FormTitle>
                <form onSubmit={handleEmailSubmit}>
                  <FormGroup>
                    <Label htmlFor="emailAtual">E-mail atual</Label>
                    <Input
                      type="email"
                      id="emailAtual"
                      name="emailAtual"
                      value={emailForm.emailAtual}
                      onChange={handleEmailChange}
                      required
                      readOnly
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="novoEmail">Novo e-mail</Label>
                    <Input
                      type="email"
                      id="novoEmail"
                      name="novoEmail"
                      value={emailForm.novoEmail}
                      onChange={handleEmailChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="confirmarEmail">Redigite o novo e-mail</Label>
                    <Input
                      type="email"
                      id="confirmarEmail"
                      name="confirmarEmail"
                      value={emailForm.confirmarEmail}
                      onChange={handleEmailChange}
                      required
                    />
                  </FormGroup>
                  <ButtonContainer>
                    <SaveButton type="submit" disabled={isSubmittingEmail}>
                      {isSubmittingEmail ? 'Salvando...' : 'Salvar'}
                    </SaveButton>
                  </ButtonContainer>
                </form>
              </FormSection>
            </SectionContainer>
          </ContentContainer>
        </MainContent>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default EditarConta;

